import React, { FC } from 'react'
import { useTranslation } from 'gatsby-plugin-react-i18next'
import CaseStudiesGridItem from 'src/components/sections/caseStudies/CaseStudiesGridItem'
import SectionHeader from 'src/components/ui/SectionHeader'

import { directusFileUrl } from 'src/helpers/directus'
import { GLOBAL_PREVIEW_MODE, reorderCasesForPreview, getCaseStudyCoverUrl } from 'src/helpers/preview'


export interface ICaseStudiesSectionProps {
  items: any[]
  className?: string
  isPreview?: boolean
  descriptionWidthClass?: string
}

const CaseStudiesSection: FC<ICaseStudiesSectionProps> = ({
  items,
  className = '',
  isPreview,
  descriptionWidthClass = 'md:w-6/12',
}) => {
  const isPreviewMode = isPreview !== undefined ? isPreview : GLOBAL_PREVIEW_MODE
  const { t } = useTranslation('case-studies')

  const orderedItems = isPreviewMode ? reorderCasesForPreview(items) : (items || [])
  const validItems = (orderedItems || []).filter((item: any) => item?.t?.length > 0)
  // Pobieramy 4 pierwsze projekty z listy
  const latestItems = validItems.slice(0, 4)

  return (
    <section className={`container py-20 md:py-32 bg-transparent ${className}`}>
      <SectionHeader
        subtitle={t('subtitle')}
        titlePart1={t('firstPartOfTitle')}
        titlePart2={t('secondPartOfTitle')}
        description={t('Overview of case studies')}
        descriptionWidthClass={descriptionWidthClass}
        ctaText={t('view selected work') || 'view selected work'}
        ctaLink="/case-studies"
        className="mb-12 md:mb-20"
        ctaVariant='link'
      />

      {/* Grid of 4 latest items with diverse mapping from case-studies page */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-x-12 gap-y-16">
        {latestItems.map((caseStudy, index) => {
          const translation = caseStudy?.t?.[0] || {}
          const {
            firstPartOfTitle = '',
            secondPartOfTitle = '',
            contentHeader = '',
            excerpt = '',
          } = translation

          let span = ''
          const totalItems = latestItems.length
          const isOdd = totalItems % 2 !== 0

          if (isOdd && index === totalItems - 1) {
            span = 'md:col-span-12'
          } else {
            const pairIndex = Math.floor(index / 2)
            const isFirstInPair = index % 2 === 0
            if (pairIndex % 2 === 0) {
              span = isFirstInPair ? 'md:col-span-5' : 'md:col-span-7'
            } else {
              span = isFirstInPair ? 'md:col-span-7' : 'md:col-span-5'
            }
          }

          const imageUrl = !isPreviewMode && caseStudy.desktopImage?.id
            ? directusFileUrl(caseStudy.desktopImage.id)
            : getCaseStudyCoverUrl(caseStudy)

          const detailImageUrl = !isPreviewMode && caseStudy.detailsDesktopImage?.id
            ? directusFileUrl(caseStudy.detailsDesktopImage.id)
            : undefined

          return (
            <CaseStudiesGridItem
              key={caseStudy.id || index}
              title={contentHeader || ''}
              subtitle={excerpt || ''}
              firstPartOfTitle={firstPartOfTitle}
              secondPartOfTitle={secondPartOfTitle}
              tags={caseStudy.platforms || []}
              ctaLink={`/case-studies${caseStudy.slug}`}
              className={span}
              isFullWidth={isOdd && index === totalItems - 1}
              image={{
                url: imageUrl,
                alt: `${firstPartOfTitle} ${secondPartOfTitle}`,
              }}
              detailImage={
                detailImageUrl
                  ? {
                      url: detailImageUrl,
                      alt: `${firstPartOfTitle} ${secondPartOfTitle}`,
                    }
                  : undefined
              }
            />
          )
        })}
      </div>
    </section>
  )
}

CaseStudiesSection.defaultProps = {
  isPreview: undefined,
  descriptionWidthClass: 'md:w-6/12',
}

export default CaseStudiesSection
